import { createContext, useContext } from 'react';
import { none } from 'fp-ts/Option';
import {
  BrandAffinityInsightsDataManagementContext,
  CalculatedDeltas,
  CampaignImpactAffinityLevels,
  CampaignImpactChartData,
  CsvData,
  GraphHistory,
  Header,
  MovementByCohortMetrics,
  Timespan,
  TrendTypeEnum,
  CalculatedTopCampaign,
  EngagementMetricEnum,
  CalculatedEngagementMetrics,
  CalculatedUsersMetrics,
  UnsubMessage,
  DeltaDirectionEnum,
} from 'modules/intelligentInsights/brandAffinityInsights/types';
import { TimespanEnum } from 'modules/intelligentInsights/types';
import { BrandAffinityLevel } from 'modules/domains';

export const defaultDataManagementContext = {
  managedInsightsData: {
    history: none,
    deltas: none,
    engagementMetrics: none,
    campaignImpact: none,
    usersMetrics: none,
    unscoredMetrics: none,
    calculatedMovementMetrics: none,
  },
  timeSpan: {
    selectedTimespan: TimespanEnum.Thirty,
    // biome-ignore lint/correctness/noUnusedVariables:
    updateTimespan: (nextTimespan: Timespan) => null,
  },
  // biome-ignore lint/correctness/noUnusedVariables:
  getHistoryCSVData: (trendType: TrendTypeEnum) => ({
    headers: [] as Header[],
    csvData: [] as CsvData[],
  }),
  affinityBreakdownCSVData: {
    engagementData: {
      headers: [] as Header[],
      csvData: [] as CsvData[],
    },
    movementData: {
      headers: [] as Header[],
      csvData: [] as CsvData[],
    },
    campaigns: {
      headers: [] as Header[],
      csvData: [] as CsvData[],
    },
  },
};

export const defaultHistory: GraphHistory = {
  categories: [],
  byCount: {
    historyPoints: [],
  },
  byPercent: {
    historyPoints: [],
  },
};

export const defaultCalculateMovementMetrics: MovementByCohortMetrics[] = [
  {
    cohort: BrandAffinityLevel.LOYAL,
    deletedUsers: 0,
    newUsersCohort: {},
    newUsersToProject: 0,
    totalNewUsers: 0,
    totalRetainedUsers: 0,
    totalUsersLeft: 0,
    usersLeftCohort: {},
  },
];

export const defaultUnscored = {
  brandAffinityLabel: BrandAffinityLevel.UNSCORED,
  delta: 0,
  percent: 0,
};
export const defaultDeltas: CalculatedDeltas = {
  byCount: {
    gain: { brandAffinityLabel: BrandAffinityLevel.POSITIVE, delta: 0, percent: 0 },
    decline: { brandAffinityLabel: BrandAffinityLevel.NEGATIVE, delta: 0, percent: 0 },
    unscored: { brandAffinityLabel: BrandAffinityLevel.UNSCORED, delta: 0, percent: 0 },
  },
  byPercent: {
    gain: { brandAffinityLabel: BrandAffinityLevel.POSITIVE, delta: 0, percent: 0 },
    decline: { brandAffinityLabel: BrandAffinityLevel.NEGATIVE, delta: 0, percent: 0 },
    unscored: { brandAffinityLabel: BrandAffinityLevel.UNSCORED, delta: 0, percent: 0 },
  },
};
export const defaultCalculatedDeltas = {
  byCount: {
    ...defaultDeltas.byCount,
    [DeltaDirectionEnum.unscored]: defaultUnscored,
  },
  byPercent: {
    ...defaultDeltas.byPercent,
    [DeltaDirectionEnum.unscored]: defaultUnscored,
  },
};

export const defaultCalculatedUsersMetrics = Object.values(BrandAffinityLevel).reduce(
  (accData, level) => {
    return {
      ...accData,
      [level]: {
        history: {
          count: 0,
          percent: 0,
        },
        delta: {
          count: 0,
          percent: 0,
        },
      },
    };
  },
  {} as CalculatedUsersMetrics,
);

export const defaultCalculatedEngagementMetrics = Object.values(BrandAffinityLevel).reduce(
  (accData, level) => {
    return {
      ...accData,
      [level]: {
        overview: {
          [EngagementMetricEnum.Clicks]: 0,
          [EngagementMetricEnum.Opens]: 0,
          [EngagementMetricEnum.Sends]: 0,
        },
      },
    };
  },
  {} as CalculatedEngagementMetrics,
);
// biome-ignore lint/correctness/noUnusedVariables:
export const defaultCampaignChartDataCreator = (label: CampaignImpactAffinityLevels) =>
  ({ mediums: [], chartData: [] }) as CampaignImpactChartData;
// biome-ignore lint/correctness/noUnusedVariables:
export const defaultTopCampaignsDataCreator = (label: CampaignImpactAffinityLevels) => ({
  all: [] as Array<CalculatedTopCampaign>,
  down: [] as Array<CalculatedTopCampaign>,
  up: [] as Array<CalculatedTopCampaign>,
});
export const defaultCampaignImpactCreators = {
  getCampaignChartData: defaultCampaignChartDataCreator,
  getSortedCampaigns: defaultTopCampaignsDataCreator,
};

export const defaultUnscoredMetrics = {
  totalSends: 0,
  totalSendSkips: 0,
  uniqueUsersUnsub: 0,
  topUnsubMessageTypeIds: [] as Array<UnsubMessage>,
  topUnsubChannelIds: [] as Array<UnsubMessage>,
};

export const DataManagementContext = createContext<BrandAffinityInsightsDataManagementContext>(
  defaultDataManagementContext,
);
export const useDataManagementContext = () => useContext(DataManagementContext);
